import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOverlayContext } from 'seagull';
import { isValidHexColor } from '~/business-logic/colorsLogic';
import { useStoreActions } from '~/contexts';
import { useUploadLogo } from '~/hooks/useUploadLogo';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { makeDefaultBranding } from '~/store/store.utils';
export function useBrandSettingsForm(param) {
    let { worksAt, silent } = param;
    const { api, setBranding, logBI } = useStoreActions();
    const { showSuccessToast } = useOverlayContext();
    const { uploadLogo, savingLogo } = useUploadLogo(worksAt);
    const { t: brandSettingsTexts } = useLocaleKeys('pages.businessDetails.brandSettings');
    const initialBranding = useMemo(()=>{
        var _worksAt_branding;
        return (_worksAt_branding = worksAt.branding) !== null && _worksAt_branding !== void 0 ? _worksAt_branding : makeDefaultBranding();
    }, [
        worksAt.branding
    ]);
    const initialLogo = useMemo(()=>{
        var _worksAt_logoUrl;
        return {
            file: null,
            asDataURL: (_worksAt_logoUrl = worksAt.logoUrl) !== null && _worksAt_logoUrl !== void 0 ? _worksAt_logoUrl : null
        };
    }, [
        worksAt.logoUrl
    ]);
    const [branding, setLocalBranding] = useState(initialBranding);
    const [logo, setLogo] = useState(initialLogo);
    const [saving, setSaving] = useState(false);
    useEffect(()=>{
        setLocalBranding(initialBranding);
    }, [
        initialBranding
    ]);
    useEffect(()=>{
        setLogo(initialLogo);
    }, [
        initialLogo
    ]);
    const brandingDirty = useMemo(()=>branding.buttonPrimary !== initialBranding.buttonPrimary || branding.backgroundGradientStart !== initialBranding.backgroundGradientStart || branding.backgroundGradientEnd !== initialBranding.backgroundGradientEnd || branding.backgroundStandard !== initialBranding.backgroundStandard, [
        branding,
        initialBranding
    ]);
    const logoDirty = logo.file !== null;
    const isDirty = brandingDirty || logoDirty;
    const errors = useMemo(()=>new Set(Object.entries(branding).filter((param)=>{
            let [, value] = param;
            return !isValidHexColor(value);
        }).map((param)=>{
            let [key] = param;
            return key;
        })), [
        branding
    ]);
    const isValid = errors.size === 0;
    const save = useCallback(async ()=>{
        if (!isDirty) return;
        if (!silent) setSaving(true);
        logBI(10420);
        try {
            if (logoDirty && logo.file && logo.asDataURL) {
                const { success } = await uploadLogo({
                    file: logo.file,
                    asDataURL: logo.asDataURL
                });
                if (!success) return;
            }
            if (brandingDirty) {
                const { success } = await handleRequest(api.user.updateBranding(worksAt.id, branding));
                if (!success) return;
                setBranding(branding);
            }
            if (!silent) showSuccessToast(brandSettingsTexts.savedSuccessfully(), 'brand-settings-success-update-toast');
            setLogo({
                file: null,
                asDataURL: logo.asDataURL
            });
        } finally{
            if (!silent) setSaving(false);
        }
    }, [
        isDirty,
        logoDirty,
        brandingDirty,
        logo,
        branding,
        uploadLogo,
        api,
        worksAt.id,
        setBranding,
        showSuccessToast,
        silent,
        logBI
    ]);
    return {
        branding,
        setBranding: setLocalBranding,
        logo,
        setLogo,
        isDirty,
        isValid,
        errors,
        save,
        saving: saving || savingLogo
    };
}
